import * as analytics from '@cartwheel/analytics'
import { Controller } from '@hotwired/stimulus'

const WARNING_IGNORED_EVENT = 'UPDATE_INSURANCE_MEMBER_ID_FORMAT_WARNING_IGNORED'

/**
 * Payer-specific Member ID format guidance, added as part of the Nirvana Pilot (RCM-1316).
 * This JS-side mapping is intentionally lightweight for the pilot. If format guidance is
 * retained beyond the pilot, the mapping should move to a more permanent solution
 * (e.g., payer configuration in the database or a shared backend constant).
 */
const PAYER_FORMATS = [
  {
    key: 'aetna',
    names: ['aetna'],
    regex: /^([A-Z]\d{8,9}|\d{9,10})$/,
    placeholder: 'W123456789',
  },
  {
    // UnitedHealthcare and Optum share the same regex
    key: 'uhc_optum',
    names: [
      'unitedhealthcare',
      'optumhealth',
      'optum_health',
      'united_optum',
      'mass_general_brigham',
      'united_health_care',
    ],
    regex: /^([A-Z]\d{8,9}|\d{9,12})$/,
    placeholder: 'R123456789',
  },
  {
    key: 'cigna',
    names: ['cigna'],
    regex: /^[Uu]?\d{9,12}$/,
    placeholder: 'U1234567890',
  },
  {
    // BCBS GA (Anthem Georgia Commercial) — RCM-1784. Must precede bcbs_anthem so the
    // GA-specific regex matches before the generic catch-all. Analysis of 64 Apero records:
    //   44%: 4 letters + 3 digits + letter + 4 digits (SHBP state employee format)
    //   19%: 3 letters + 3 digits + letter + 5 digits (commercial plans)
    //   19%: 3 letters + 9 digits (overlaps generic bcbs_anthem)
    key: 'bcbs_ga',
    names: ['anthem_georgia'],
    regex: /^([A-Za-z]{4}\d{3}[A-Za-z]\d{4}|[A-Za-z]{3}\d{3}[A-Za-z]\d{5}|[A-Za-z]{3}\d{9})$/,
    placeholder: 'ABCD123E4567',
  },
  {
    key: 'bcbs_anthem',
    names: ['blue_cross', 'bcbs', 'anthem'],
    regex: /^[A-Za-z]{3}\d{9,12}$/,
    placeholder: 'HVA123456789',
  },
  {
    key: 'harvard_pilgrim',
    names: ['harvard_pilgrim'],
    regex: /^[Hh][A-Za-z0-9]{8,11}$/,
    placeholder: 'HP123456789',
  },
  {
    // CareSource shares the same 9–12 digit numeric format as WellSense
    key: 'wellsense',
    names: ['wellsense', 'well_sense', 'caresource'],
    regex: /^\d{9,12}$/,
    placeholder: '123456789',
  },
  {
    // Standard format is HCIA + 8 digits; A + 8 digits is a rare variant seen in production
    key: 'health_choice_az',
    names: ['health_choice'],
    regex: /^(HCIA|A)\d{8}$/,
    placeholder: 'HCIA12345678',
  },
  {
    // Two formats in production: CD + 9 digits (e.g. CD129937600) and
    // D + 2 letters + 5 digits + letter + 2 digits (e.g. DFH97104F00)
    key: 'cdphp',
    names: ['cdphp'],
    regex: /^(CD\d{9}|D[A-Z]{2}\d{5}[A-Z]\d{2})$/,
    placeholder: 'CD123456789',
  },
  {
    key: 'mvp_health_care',
    names: ['mvp'],
    regex: /^\d{11}$/,
    placeholder: '12345678901',
  },
  {
    // UMR is a UnitedHealthcare subsidiary; formats mirror UHC but also include
    // 8-digit numeric and 8-digit+W suffix variants seen in production
    key: 'umr',
    names: ['umr'],
    regex: /^([A-Z]\d{8,9}|\d{8,12}|\d{8}W)$/,
    placeholder: '12345678',
  },
  {
    key: 'wellpoint',
    names: ['wellpoint'],
    regex: /^\d{3}[A-Z]\d{5}$/,
    placeholder: '123M45678',
  },
  {
    key: 'neighborhood_health_ri',
    names: ['neighborhood_health_plan_of_rhode_island'],
    regex: /^\d{9,11}$/,
    placeholder: '123456789',
  },
  {
    key: 'fidelis',
    names: ['fidelis'],
    regex: /^\d{9,11}$/,
    placeholder: '12345678901',
  },
  {
    // Carelon and ValueOptions share the same regex
    key: 'carelon',
    names: ['carelon', 'valueoptions', 'value_options'],
    regex: /^(PUM\d{9}|\d{8,13})$/,
    placeholder: 'PUM123456789',
  },
  {
    // Meridian and Molina both use 9-digit format
    key: 'meridian_molina',
    names: ['meridian', 'molina'],
    regex: /^\d{9}$/,
    placeholder: '123456789',
  },
  {
    key: 'az_complete',
    names: ['az_complete', 'arizona_complete'],
    regex: /^A\d{8}$/,
    placeholder: 'A12345678',
  },
  {
    key: 'community_care',
    names: ['community_care'],
    regex: /^\d{8,11}$/,
    placeholder: '12345678',
  },
  {
    key: 'tufts',
    names: ['tufts'],
    regex: /^(\d{4}[A-Za-z]\d{6}|[A-Za-z]{1,2}\d{9,10})$/,
    placeholder: '1234A567890',
  },
  {
    // NH Healthy Families (Ambetter NH Medicaid). All 30 passing Apero records are 11-digit numeric.
    key: 'ambetter_nh',
    names: ['nh_healthy_families'],
    regex: /^\d{11}$/,
    placeholder: '12345678901',
  },
]

function normalizeName(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_')
}

function findFormat(selectedName) {
  const normalized = normalizeName(selectedName)
  return PAYER_FORMATS.find((f) => f.names.some((n) => normalized.includes(n)))
}

export { normalizeName, findFormat }

export default class extends Controller {
  static targets = ['payerSelect', 'memberIdInput', 'hint', 'warning', 'warningText']
  static values = { hints: Object, warningTemplate: String, clientId: String }

  connect() {
    this.debounceTimer = null
    this.currentFormat = null
    // Initialize in case a payer is already selected on page load
    this.onPayerChange()

    this.abortController = new AbortController()
    const form = this.element.closest('form')
    if (form) {
      // Fire synchronously on submit. Turbo intercepts the form via preventDefault + fetch,
      // so we can't gate on event.defaultPrevented without losing every Turbo-handled submit.
      // The analytics semantics here are "user hit submit with warning visible" — still true
      // even if the submission later fails server-side or is cancelled by another listener.
      form.addEventListener('submit', () => this._onFormSubmit(), {
        signal: this.abortController.signal,
      })
    }
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    this.abortController?.abort()
  }

  _onFormSubmit() {
    if (!this._isWarningVisible()) return

    analytics.track(WARNING_IGNORED_EVENT, {
      clientId: this.hasClientIdValue ? this.clientIdValue : undefined,
      payerKey: this.currentFormat?.key,
      payerName: this.payerSelectTarget.selectedOptions[0]?.text,
    })
  }

  _isWarningVisible() {
    return this.hasWarningTarget && !this.warningTarget.classList.contains('hidden')
  }

  onPayerChange() {
    const selectedOption = this.payerSelectTarget.selectedOptions[0]
    const selectedName = selectedOption?.text ?? ''
    this.currentFormat = findFormat(selectedName)

    if (this.currentFormat) {
      this.memberIdInputTarget.placeholder = this.currentFormat.placeholder
      this.hintTarget.textContent = this.hintsValue[this.currentFormat.key] || ''
      this.hintTarget.classList.remove('hidden')
    } else {
      this.memberIdInputTarget.placeholder = ''
      this.hintTarget.classList.add('hidden')
    }

    clearTimeout(this.debounceTimer)
    const value = this.memberIdInputTarget.value.trim()
    if (this.currentFormat && value) {
      this.validate()
    } else {
      this._clearWarning()
    }
  }

  onInput() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.validate(), 500)
  }

  validate() {
    if (!this.currentFormat) return

    const value = this.memberIdInputTarget.value.trim()
    if (!value) {
      this._clearWarning()
      return
    }

    if (this.currentFormat.regex.test(value)) {
      this._clearWarning()
    } else {
      const payerName = this.payerSelectTarget.selectedOptions[0]?.text ?? 'this payer'
      const hint = this.hintsValue[this.currentFormat.key] || ''
      this.warningTextTarget.textContent = this.warningTemplateValue
        .replace('%{payer}', payerName)
        .replace('%{hint}', hint)
      this.warningTarget.classList.remove('hidden')
      this.memberIdInputTarget.classList.add('border-red-700')
    }
  }

  _clearWarning() {
    this.warningTarget.classList.add('hidden')
    this.memberIdInputTarget.classList.remove('border-red-700')
  }
}
